import { useAtom } from "@dbeining/react-atom";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Col, Container, Row } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { userAtom } from "../../atoms";
import { ConfirmationModel } from "../../components/features/common";
import { ResponsiveBreadcrumb } from "../../components/Layout/ResponsiveBreadcrumb";
import { ColumnRender, FilterType, IColumn, IFilter, IPagedListAction, SortOrder } from "../../components/UI/Declarations";
import { PagedList } from "../../components/UI/PagedList/PagedList";
import type { IHaulierListRow, ISiteOption } from "../../customTypings/haulier/Haulier";
import type { IRole } from "../../customTypings/user/Role";
import { sitesService } from "../../services/sites/sites.service";
import { hauliersService } from "../../services/hauliers/hauliers.service";
import { addEntityActionLabel, buildRecordLifecycleConfirmProps, dateRangeFilters, EntityLabels, formatPostalAddress, getUserFacingErrorMessage, resolveActionErrorMessage, includeDeletedSitesAdminFilter, ListPageTitles, logErrorForDev, recordLifecycleListConfirmBodies, relatedEntityTextColumn, resolveSingleSiteIdPin, Role, SectionLabels, showToastSuccess, SITE_RELATED_ENTITY_LIFECYCLE, appRoutes } from "../../utils";
import {
    buildHaulierAdminPagedListActions,
    canMutateHauliers,
    getHaulierAdminActionSuccessMessage,
    type HaulierAdminConfirmKind,
} from "./haulierAdminListActions";

type ConfirmKind = HaulierAdminConfirmKind | null;

const HauliersListPage = () => {
    const navigate = useNavigate();
    const currentUser = useAtom(userAtom);
    const roleNames = currentUser?.identity.roles.map((r) => r.name) ?? [];
    const canMutate = canMutateHauliers(roleNames);
    const isAdmin = currentUser?.identity.roles.some(
        (role: IRole) => role.name === Role.Admin || role.name === Role.SystemAdmin
    );

    const [sites, setSites] = useState<ISiteOption[]>([]);
    const [sitesLoading, setSitesLoading] = useState(true);
    const [sitesError, setSitesError] = useState<string | null>(null);
    const showSiteColumn = useMemo(() => sites.length > 1, [sites.length]);

    const [refreshNumber, setRefreshNumber] = useState(0);
    const [actionError, setActionError] = useState<string | null>(null);
    const [confirmKind, setConfirmKind] = useState<ConfirmKind>(null);
    const [confirmHaulierId, setConfirmHaulierId] = useState<string | null>(null);
    const [confirmHaulierSiteId, setConfirmHaulierSiteId] = useState<string | null>(null);
    const [confirmHaulierLabel, setConfirmHaulierLabel] = useState("");

    const singleSiteId = sites.length === 1 ? sites[0].id : null;

    useEffect(() => {
        sitesService
            .listForOrganisation()
            .then((list) => {
                setSites(list);
                setSitesError(null);
            })
            .catch((e: unknown) => {
                logErrorForDev("HauliersListPage.sites", e);
                setSitesError(getUserFacingErrorMessage(e, "Could not load sites."));
            })
            .finally(() => setSitesLoading(false));
    }, []);

    const closeConfirm = () => {
        setConfirmKind(null);
        setConfirmHaulierId(null);
        setConfirmHaulierSiteId(null);
        setConfirmHaulierLabel("");
    };

    const runConfirmedAction = () => {
        if (!confirmHaulierId || !confirmKind || !confirmHaulierSiteId) return;
        const id = confirmHaulierId;
        const siteId = confirmHaulierSiteId;
        const op =
            confirmKind === "disable"
                ? hauliersService.disable(siteId, id)
                : confirmKind === "enable"
                  ? hauliersService.enable(siteId, id)
                  : confirmKind === "softDelete"
                    ? hauliersService.softDelete(siteId, id)
                    : hauliersService.restore(siteId, id);
        op.then(() => {
            showToastSuccess(getHaulierAdminActionSuccessMessage(confirmKind, confirmHaulierLabel));
            setActionError(null);
            setRefreshNumber((n) => n + 1);
            closeConfirm();
        }).catch((e: unknown) => {
            setActionError(resolveActionErrorMessage(e));
            closeConfirm();
        });
    };

    const openConfirm = (kind: ConfirmKind, item: IHaulierListRow) => {
        setConfirmKind(kind);
        setConfirmHaulierId(item.id);
        setConfirmHaulierSiteId(item.siteId);
        setConfirmHaulierLabel(item.name);
    };

    const confirmProps = useMemo(
        () =>
            buildRecordLifecycleConfirmProps(
                confirmKind,
                EntityLabels.haulier,
                confirmHaulierLabel,
                recordLifecycleListConfirmBodies(confirmHaulierLabel)
            ),
        [confirmKind, confirmHaulierLabel]
    );

    const columns: IColumn[] = useMemo(() => {
        const base: IColumn[] = [];
        if (showSiteColumn) {
            base.push(
                relatedEntityTextColumn({
                    member: "siteName",
                    title: "Site",
                    width: 160,
                    relatedEntityLifecycle: SITE_RELATED_ENTITY_LIFECYCLE,
                })
            );
        }
        base.push(
            { member: "name", renderAs: ColumnRender.text, title: "Name", width: 200 } as IColumn,
            {
                member: "address",
                renderAs: ColumnRender.customRenderer,
                title: "Address",
                width: 260,
                customRenderer: (item: IHaulierListRow) => (
                    <td className="text-start text-muted small">{formatPostalAddress(item) || "-"}</td>
                ),
            } as IColumn,
            {
                member: "carrierLicenceNumber",
                renderAs: ColumnRender.text,
                title: "Licence",
                width: 140,
            } as IColumn,
            {
                member: "carrierLicenceExpiryUtc",
                renderAs: ColumnRender.date,
                title: "Licence expiry",
                width: 130,
            } as IColumn,
            { member: "isEnabled", renderAs: ColumnRender.checkCross, title: "Enabled", width: 90, align: "center" } as IColumn
        );
        return base;
    }, [showSiteColumn]);

    const siteFilterOptions = useMemo(() => {
        const opts: Record<string, string | null> = { "All sites": null };
        for (const s of sites) {
            opts[s.siteName] = s.id;
        }
        return opts;
    }, [sites]);

    const filters: IFilter[] = useMemo(() => {
        const list: IFilter[] = [];
        if (sites.length > 1) {
            list.push({
                title: "Site",
                member: "siteId",
                type: FilterType.select,
                options: siteFilterOptions,
                defaultValue: null,
            } as IFilter);
        }
        list.push(
            { title: "Name", member: "nameSearch", type: FilterType.text } as IFilter,
            { title: "Address", member: "addressSearch", type: FilterType.text } as IFilter,
            { title: "Licence", member: "carrierLicenceSearch", type: FilterType.text } as IFilter,
            ...dateRangeFilters("Licence expiry", "carrierLicenceExpiryFromUtc", "carrierLicenceExpiryToUtc"),
            {
                title: "Enabled",
                member: "isEnabled",
                type: FilterType.select,
                options: { All: null, Yes: true, No: false },
            } as IFilter,
            {
                title: "Deleted",
                member: "isDeleted",
                type: FilterType.select,
                options: { All: null, Yes: true, No: false },
                defaultValue: false,
            } as IFilter,
            ...(isAdmin ? [includeDeletedSitesAdminFilter()] : [])
        );
        return list;
    }, [sites.length, siteFilterOptions, isAdmin]);

    const resolveStaticFilterValues = useCallback(
        (filterData: Record<string, unknown>) =>
            resolveSingleSiteIdPin(filterData, sites.length, singleSiteId),
        [sites.length, singleSiteId]
    );

    const itemActions: IPagedListAction[] = useMemo(() => {
        if (!canMutate) return [];
        return [
            {
                text: "Edit",
                onClick: (item: IHaulierListRow) =>
                    navigate(`/hauliers/${item.id}`, { state: { siteId: item.siteId } }),
                primary: true,
            } as IPagedListAction,
            ...buildHaulierAdminPagedListActions((kind, item) => openConfirm(kind, item)),
        ];
    }, [canMutate, navigate]);

    const headActions: IPagedListAction[] = useMemo(() => {
        if (!canMutate) return [];
        return [
            {
                text: addEntityActionLabel(EntityLabels.haulier),
                icon: faPlus,
                onClick: () => navigate("/hauliers/new"),
                primary: true,
            } as IPagedListAction,
        ];
    }, [canMutate, navigate]);

    return (
        <Container fluid>
            <Helmet>
                <title>{ListPageTitles.hauliers}</title>
            </Helmet>
            <Row className="mb-3">
                <Col>
                    <ResponsiveBreadcrumb
                        items={[
                            { text: SectionLabels.movementParties, to: appRoutes.transportParties },
                            { text: ListPageTitles.hauliers },
                        ]}
                    />
                    <h1>{ListPageTitles.hauliers}</h1>
                    <p className="text-muted">
                        Waste carriers who transport material to and from your sites (name, address, optional carrier
                        licence). Duplicate rules apply within each site only.
                    </p>
                    {sitesError && <Alert variant="danger">{sitesError}</Alert>}
                    {actionError && (
                        <Alert variant="danger" className="mt-2" onClose={() => setActionError(null)} dismissible>
                            {actionError}
                        </Alert>
                    )}
                </Col>
            </Row>
            <Row>
                <Col>
                    {sitesLoading ? (
                        <Alert variant="info">Loading sites…</Alert>
                    ) : sites.length === 0 ? (
                        <Alert variant="warning">No sites are available for your account.</Alert>
                    ) : (
                        <PagedList
                            key={showSiteColumn ? "multi-site" : "single-site"}
                            columns={columns}
                            filters={filters}
                            resolveStaticFilterValues={resolveStaticFilterValues}
                            headActions={headActions}
                            itemActions={itemActions}
                            rowActionsAriaLabel="Haulier actions"
                            showDeletedRowIndicator
                            defaultSortBy={showSiteColumn ? "SiteName" : "Name"}
                            defaultSortOrder={SortOrder.ascending}
                            getUrl="/api/hauliers/all"
                            rowFormatter={() => null}
                            persistanceKey="HauliersListPage"
                            refreshNumber={refreshNumber}
                        />
                    )}
                </Col>
            </Row>

            <ConfirmationModel
                show={confirmKind !== null}
                onClose={(confirmed) => {
                    if (confirmed) runConfirmedAction();
                    else closeConfirm();
                }}
                title={confirmProps.title}
                body={confirmProps.body}
                buttonSuccessVariant={confirmProps.buttonSuccessVariant}
                buttonSuccessText={confirmProps.buttonSuccessText}
                buttonCancelText="Cancel"
            />
        </Container>
    );
};

export { HauliersListPage };
